import { Component, OnInit } from '@angular/core';
import { ChargebeeService } from '../../../core/services/chargebee.service';
import { EncryptionService } from '../../../core/services/encryption';
import { SETTINGS } from '../../../core/config/common.settings';
import { MessageService } from 'primeng/api';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { UserService } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TermsConditionsModalComponent } from '../../user/terms-conditions-modal/terms-conditions-modal.component';

declare var Chargebee: any;

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    ButtonModule,
    TermsConditionsModalComponent
  ],
  providers: [MessageService],
})
export class SubscriptionComponent implements OnInit {
  org: any;
  listOfPlans: any [] = [];
  loading = false;
  buttonLoadingStates: { [key: string]: boolean } = {};
  showTermsModal: boolean = false;
  termsAccepted: boolean = false;
  canSelectPackage: boolean = false;

  constructor(
    private chargebeeService: ChargebeeService,
    private subscriberService: SubscriberService,
    private encryption: EncryptionService,
    private messageService: MessageService,
    private userService: UserService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    // Add a small delay before checking terms to allow backend to process any recent updates
    setTimeout(() => {
      this.checkTermsAcceptance();
    }, 500);
    // Load plans
    this.chargebeeService.getListOfPlansByProductId().then(r => {
      this.loading = false;
      this.listOfPlans = r;
    } );
  }

  checkTermsAcceptance(): void {
    this.userService.getCurrentUserProfile().subscribe({
      next: (user: any) => {
        this.termsAccepted = user.termsAccepted === true;
        if (!this.termsAccepted) {
          // Show modal if terms not accepted
          this.showTermsModal = true;
          this.canSelectPackage = false;
        } else {
          this.canSelectPackage = true;
        }
      },
      error: (error) => {
        console.error('Error checking terms acceptance:', error);
        // Retry once after a delay if there's an error (might be a timing issue)
        setTimeout(() => {
          this.userService.getCurrentUserProfile().subscribe({
            next: (user: any) => {
              this.termsAccepted = user.termsAccepted === true;
              if (!this.termsAccepted) {
                this.showTermsModal = true;
                this.canSelectPackage = false;
              } else {
                this.canSelectPackage = true;
              }
            },
            error: (retryError) => {
              console.error('Error checking terms acceptance on retry:', retryError);
              // If error persists, show modal to be safe
              this.showTermsModal = true;
              this.canSelectPackage = false;
            }
          });
        }, 1000);
      }
    });
  }

  onTermsAccepted(): void {
    // Save terms acceptance
    this.userService.acceptTermsAndConditions()
      .subscribe({
        next: (response: any) => {
          // Update local user object with the updated user from response
          if (response?.user) {
            this.authService.setLoggedInUser(JSON.stringify(response.user));
          }
          
          // Refresh user profile to ensure we have the latest data
          this.userService.getCurrentUserProfile().subscribe({
            next: (updatedUser) => {
              // Update localStorage with refreshed user data
              this.authService.setLoggedInUser(JSON.stringify(updatedUser));
              
              this.termsAccepted = updatedUser.termsAccepted === true;
              this.canSelectPackage = true;
              this.showTermsModal = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Terms and conditions accepted successfully.'
              });
            },
            error: (refreshError) => {
              console.error('Error refreshing user profile:', refreshError);
              // Still update local state if refresh fails
              this.termsAccepted = true;
              this.canSelectPackage = true;
              this.showTermsModal = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Terms and conditions accepted successfully.'
              });
            }
          });
        },
        error: (error) => {
          console.error('Error accepting terms:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save terms acceptance. Please try again.'
          });
        }
      });
  }

  onTermsModalVisibleChange(visible: boolean): void {
    // If modal is closed without accepting, keep it open
    if (!visible && !this.termsAccepted) {
      this.showTermsModal = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Terms Required',
        detail: 'You must accept the Terms and Conditions to select a package.'
      });
    }
  }

  async findBrand(): Promise<any> {
    try {
      const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      if (encryptedUserStr) {
        const userStr = this.encryption.decrypt(encryptedUserStr);
        const user = JSON.parse(userStr);
        return await this.subscriberService.findSubscriber(user._id);
      }
    } catch (e) {
      console.error('Error finding brand:', e);
    }
  }

  async createdHostedPage(plan: string): Promise<void> {
    // Check if terms are accepted before proceeding
    if (!this.termsAccepted) {
      this.showTermsModal = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Terms Required',
        detail: 'You must accept the Terms and Conditions before selecting a package.'
      });
      return;
    }

    try {
      // Set loading state for this specific button
      this.buttonLoadingStates[plan] = true;
      
      Chargebee.init({
        site: SETTINGS.CHARGEBEE_SITE,
        isItemsModel: true,
      });
      const cbInstance = Chargebee.getInstance();
      const hostedPageData = await this.chargebeeService.createHostedPage(plan);
      if (hostedPageData) {
        cbInstance.openCheckout({
          hostedPage: () => {
            return Promise.resolve({ ...hostedPageData.hosted_page });
          },
          loaded: () => {
            console.log('loaded');
          },
          success: (hostedPageId: any): void => {
            console.log('Successful checkout');
            // Reset loading state before redirect
            this.buttonLoadingStates[plan] = false;
            // Redirect to your custom page after success after 2 seconds
            const baseUrl = window.location.origin;
            window.location.href = `${baseUrl}/payment/payment-success`;
          },
          error: (error: any) => {
            // Reset loading state on error
            this.buttonLoadingStates[plan] = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error?.message || error 
            });
            console.error(error);
          },
        });
      }
    } catch (error: any) {
      // Reset loading state on error
      this.buttonLoadingStates[plan] = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || error
      });
    }
  }

  convertNumber(value: any): string {
    // Check if value is a valid number
    if (value === null || value === undefined || isNaN(Number(value))) {
      console.warn('Invalid price value:', value);
      return '0,00';
    }
    
    // Convert to number and ensure it's valid
    const numValue = Number(value);
    if (isNaN(numValue)) {
      console.warn('Failed to convert price value to number:', value);
      return '0,00';
    }
    
    // Convert cents to euros and format to European number format (12.000,00)
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue / 100);
  }
}
